import { useEffect } from 'react';
import { useAppStore } from '@/store/appStore';
import type { ThemeMode, ThemePalette } from '@/components/modals/SettingsModal';

/**
* Determina il tema di sistema (light o dark) basandosi sulle preferenze del browser.
*/
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
}

/**
* Applica il tema e la palette al documento HTML.
*/
function applyTheme(theme: ThemeMode, palette: ThemePalette) {
  const root = document.documentElement;
  const effectiveTheme = theme === 'system' ? getSystemTheme() : theme;

  root.setAttribute('data-theme', effectiveTheme);
  root.setAttribute('data-palette', palette);
}

/**
* Hook per gestire il tema dell'applicazione (light/dark/system) e la palette di colori.
* Sincronizza automaticamente il tema con localStorage e ascolta i cambiamenti del tema di sistema.
* 
* @returns Oggetto con lo stato del tema e funzioni per modificarlo
* @returns theme - Tema corrente (light, dark, o system)
* @returns palette - Palette di colori corrente
* @returns setTheme - Funzione per impostare il tema
* @returns setPalette - Funzione per impostare la palette
*/
export function useTheme() {
  const theme = useAppStore((state) => state.theme);
  const palette = useAppStore((state) => state.palette);
  const setTheme = useAppStore((state) => state.setTheme);
  const setPalette = useAppStore((state) => state.setPalette);

  // Apply theme on mount and when it changes
  useEffect(() => {
  applyTheme(theme, palette);
  localStorage.setItem('zenda-theme', theme);
  localStorage.setItem('zenda-palette', palette);
  }, [theme, palette]);

  // Listen for system theme changes
  useEffect(() => {
  if (theme !== 'system') return;

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleChange = () => applyTheme(theme, palette);

  mediaQuery.addEventListener('change', handleChange);
  return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, palette]);

  return {
  theme,
  palette,
  setTheme,
  setPalette,
  };
}

